export const INGREDIENT_CATEGORY_OPTIONS = ['荤菜', '素菜', '主食干货', '调味品', '其他'] as const;
export type IngredientCategory = typeof INGREDIENT_CATEGORY_OPTIONS[number];

const EXPLICIT_INGREDIENT_CATEGORIES = new Map<string, IngredientCategory>([
  ...[
    '五花肉', '兔子肉', '千层肚', '午餐肉', '土鸡肉', '夹子肉', '排骨', '整兔',
    '牛眉毛肉', '牛肋条', '牛蛙', '牛里脊肉', '牛肉', '猪肝', '猪蹄', '皮蛋', '眉毛肉',
    '肉末', '肥肠', '腊肉', '耗儿鱼', '鱼肉', '鲫鱼', '鸡肉', '鸡胸肉', '鸡腿',
    '鸭子肉', '鸭脚', '鸭血', '鹅肉', '肚子',
  ].map((name) => [name, '荤菜'] as const),
  ...[
    '东汉菜', '丝瓜', '二荆条', '付水瓜', '儿菜', '口蘑', '四季豆', '团葱',
    '圆白菜', '土豆', '大葱', '娃娃菜', '嫩南瓜', '嫩四季豆', '嫩胡豆', '嫩茄子',
    '嫩豇豆', '子姜', '小白菜', '小葱', '小西红柿', '干黄花', '平菇', '木耳菜',
    '本地菜苔', '杏鲍菇', '水果玉米', '油麦菜', '洋葱', '活水豆花', '海带',
    '海带丝', '海鲜菇', '牛心白', '牛皮菜', '生菜', '白芹菜', '白萝卜',
    '空心菜', '瓢儿白', '紫甘蓝', '红小米辣', '红椒', '红薯尖', '绿豆芽', '老南瓜',
    '耳子', '胡萝卜', '脆莲藕', '腐竹', '花菜', '苦瓜', '苦菊', '茶树菇',
    '荷兰豆', '莲白', '莲藕', '莴笋', '莴笋头', '菜心', '菠菜', '蒜瓣',
    '蒜苔', '蒜苗', '薄皮芋儿', '藠头', '蘑菇', '虫草花', '西红柿', '西芹',
    '西葫芦', '豆干', '豆皮', '豆腐', '豆芽', '豇豆', '豌豆尖', '金针菇',
    '铁棍山药', '青小米辣', '青椒', '香芋', '香菇', '香菜', '魔芋',
    '鲜竹笋', '鲜笋子', '鲜柠檬', '黄瓜', '黄豆芽', '冬瓜',
    '糯玉米', '红苕', '紫薯', '小蜜薯', '贝贝南瓜',
  ].map((name) => [name, '素菜'] as const),
  ...[
    '凉面', '宽面', '米线', '糯小米', '红薯', '细红薯粉丝', '细面', '龙口粉丝',
    '蒸肉粉', '苕皮', '干豌豆', '干胡豆瓣', '绿豆', '黄豆', '生花生米', '花生米',
  ].map((name) => [name, '主食干货'] as const),
  ...[
    '榨菜', '咸菜', '干辣椒节', '永川豆豉', '烧白咸菜', '甜面酱',
    '芝麻味沙拉酱', '老鸭汤料包', '豆豉', '酸菜', '鱼香', '鲜花椒',
    '鲜青花椒', '黄灯笼泡椒酱', '黄豆酱', '老姜',
  ].map((name) => [name, '调味品'] as const),
  ...[
    '鸡蛋',
  ].map((name) => [name, '其他'] as const),
]);

export const PURCHASE_DISPLAY_CATEGORY_OPTIONS = ['荤菜', '素菜', '其他采购'] as const;
export type PurchaseDisplayCategory = typeof PURCHASE_DISPLAY_CATEGORY_OPTIONS[number];

export function ingredientCategoryFromPurchaseDisplay(
  category: PurchaseDisplayCategory,
): IngredientCategory {
  return category === '其他采购' ? '其他' : category;
}

export const DEFAULT_DAILY_PURCHASE_DEFAULTS: Array<{
  id?: string;
  name: string;
  quantity: string;
  unit: string;
  ingredientCategory: IngredientCategory;
  weekdayMask?: number;
}> = [
  { name: '小葱', quantity: '0.5', unit: '斤', ingredientCategory: '素菜', weekdayMask: 127 },
  { name: '蒜瓣', quantity: '1.5', unit: '斤', ingredientCategory: '素菜', weekdayMask: 127 },
  { name: '糯玉米', quantity: '8', unit: '斤', ingredientCategory: '素菜', weekdayMask: 42 },
  { name: '小蜜薯', quantity: '5', unit: '斤', ingredientCategory: '素菜', weekdayMask: 2 },
  { name: '贝贝南瓜', quantity: '5', unit: '斤', ingredientCategory: '素菜', weekdayMask: 8 },
  { name: '铁棍山药', quantity: '5', unit: '斤', ingredientCategory: '素菜', weekdayMask: 32 },
];

export function ingredientCategoryForName(name: string): IngredientCategory {
  return EXPLICIT_INGREDIENT_CATEGORIES.get(name.trim()) ?? '其他';
}

export function normalizeIngredientCategory(value: unknown, name = ''): IngredientCategory {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return (INGREDIENT_CATEGORY_OPTIONS as readonly string[]).includes(normalized)
    ? normalized as IngredientCategory
    : ingredientCategoryForName(name);
}

export interface PurchaseCategoryGroup<T> {
  category: PurchaseDisplayCategory;
  items: T[];
}

/**
 * Purchase-facing screens intentionally use three broad groups. The five
 * ingredient categories remain stored so recipe editing keeps its precision,
 * while staples, condiments and miscellaneous purchases share one output.
 */
export function purchaseDisplayCategory(
  value: unknown,
  name = '',
): PurchaseDisplayCategory {
  const category = normalizeIngredientCategory(value, name);
  if (category === '荤菜') return '荤菜';
  if (category === '素菜') return '素菜';
  return '其他采购';
}

export function groupPurchaseItemsByCategory<
  T extends { name: string; ingredientCategory?: IngredientCategory },
>(items: T[]): PurchaseCategoryGroup<T>[] {
  const groups = new Map<PurchaseDisplayCategory, T[]>(
    PURCHASE_DISPLAY_CATEGORY_OPTIONS.map((category) => [category, []]),
  );
  for (const item of items) {
    const category = purchaseDisplayCategory(item.ingredientCategory, item.name);
    groups.get(category)?.push(item);
  }
  return PURCHASE_DISPLAY_CATEGORY_OPTIONS
    .map((category) => ({ category, items: groups.get(category) ?? [] }))
    .filter((group) => group.items.length > 0);
}
